import { useEffect, useState } from "react";
import {
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Chip,
    Button,
    Stack,
    Modal,
    Fade,
    Backdrop,
} from "@mui/material";
import Layout from "../../../components/layouts/Layout";
import { User } from "../../../types/userTypes";
import AddUser from "./AddUser";
import EditUser from "./EditUser";
import ConfirmDialog from "../../../components/dialog/ConfirmationDialog";
import { toast } from "react-toastify";
import ViewUser from "./ViewUser";
import UpdateUserPassword from "./UpdatePassword";
import { useNavigate } from "react-router-dom";
import { downloadRepoZipFolder } from "../../../service/folderService";
import CompanyAdminUserService from "../../../service/companyadmin/userService";

const statusColors: Record<string, "success" | "error"> = {
    active: "success",
    inactive: "error",
};

const UserList = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [open, setOpen] = useState<boolean>(false);
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
    const [userToDelete, setUserToDelete] = useState<number | null>(null);
    const [openAddModal, setOpenAddModal] = useState<boolean>(false);
    const [openViewModal, setOpenViewModal] = useState<boolean>(false);
    const [viewUser, setViewUser] = useState<User | null>(null);
    const [loadingView, setLoadingView] = useState<boolean>(false);
    const [openEditModal, setOpenEditModal] = useState<boolean>(false);
    const [editUser, setEditUser] = useState<User | null>(null);
    //Password Modal
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [companyMap, setCompanyMap] = useState<Record<string, string>>({});

    const navigate = useNavigate();

    const fetchUsers = async () => {
        try {
            const userData = await CompanyAdminUserService.getUsersByCompany();
            setUsers(userData);
        } catch (error) {
            console.error("Failed to fetch users", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const confirmDelete = (userId: number) => {
        setUserToDelete(userId);
        setConfirmOpen(true);
    };

    const handleDelete = async () => {
        if (!userToDelete) return;
        try {
            await CompanyAdminUserService.removeUser(userToDelete);
            toast.success("User deleted successfully!");
            fetchUsers(); // Refresh user list
        } catch (error) {
            toast.error("Failed to delete user.");
            console.error("Error deleting user:", error);
        } finally {
            setConfirmOpen(false);
            setUserToDelete(null);
        }
    };

    const handleViewRepo = (userId: number, userName: string) => {
        navigate(`/folderRepo?userId=${userId}&userName=${encodeURIComponent(userName)}`);
    };


    const handleViewUser = async (user: any) => {
        setLoadingView(true);
        setOpenViewModal(true);
        try {

            setViewUser(user);
        } catch (error) {
            toast.error("Failed to load user details");
        } finally {
            setLoadingView(false);
        }
    };

    const handleEditUser = async (user: User) => {
        setEditUser(user);
        setOpenEditModal(true);
    };

    const handleOpenPasswordModal = (userId: number) => {
        setSelectedUserId(userId);
        setPasswordModalOpen(true);
    };

    const handleDownloadRepo = async (userId: number, username: string) => {
        try {
            await downloadRepoZipFolder(username, userId.toString());
            toast.success("Folder downloaded successfully!");
        } catch (error) {
            toast.error("Failed to download folder.");
        }
    };

    const handleAssignAccess = (userId: string, companyId: string) => {
        navigate(`/ca/assign-access/${userId}/${companyId}`);
    };


    return (
        <Layout>
            {/* Title & Add New User Button */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">
                    User List
                </Typography>
                <Button variant="contained" color="primary" sx={{ color: "white" }} onClick={() => setOpen(true)}>
                    Add New User
                </Button>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : (
                <>
                    {users.length === 0 ? (
                        <div className="text-center text-gray-500 py-10">
                            <p>No user.</p>
                        </div>
                    ) : (
                        <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "none" }}>
                            <Table sx={{ background: "white" }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Username</TableCell>
                                        <TableCell>Role</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Phone</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user, index) => {
                                        const mappedStatus = user.status === "1" ? "active" : "inactive";
                                        return (
                                            <TableRow key={user.id}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>{user.username}</TableCell>
                                                {(user.role === 'user' && user.is_company_admin === "1") ? (
                                                    <TableCell>
                                                        Company Admin
                                                    </TableCell>
                                                ) : (<TableCell>
                                                    User
                                                </TableCell>)}
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>{user.phone}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={mappedStatus}
                                                        color={statusColors[mappedStatus]}
                                                        sx={{ textTransform: "capitalize", fontWeight: 500 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1}>
                                                        {user.role === 'user' && (
                                                            <Button
                                                                variant="contained"
                                                                sx={{ backgroundColor: "#28a745", color: "white" }}
                                                                onClick={() => handleAssignAccess(user.id.toString(), user.company_id)}
                                                            >
                                                                Assign Access
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="contained"
                                                            sx={{ backgroundColor: "#6c757d", color: "white" }}
                                                            onClick={() => handleViewUser(user)}
                                                        >
                                                            View
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            sx={{ backgroundColor: "#6c757d", color: "white" }}
                                                            onClick={() => handleEditUser(user)}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            sx={{ color: "white" }}
                                                            onClick={() => handleOpenPasswordModal(user.id)}
                                                        >
                                                            Password
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            sx={{ backgroundColor: "#dc3545", color: "white" }}
                                                            onClick={() => confirmDelete(user.id)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}

            {/* Add User Modal */}
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
            >
                <Fade in={open}>
                    <Box sx={modalStyle}>
                        <AddUser onClose={() => { setOpen(false); fetchUsers(); }} />
                    </Box>
                </Fade>
            </Modal>

            {/* View User Modal */}
            <ViewUser open={openViewModal} onClose={() => setOpenViewModal(false)} user={viewUser} loading={loadingView} />

            {/* Edit User Modal */}
            <EditUser open={openEditModal} onClose={() => setOpenEditModal(false)} user={editUser} refreshUsers={fetchUsers} />

            {selectedUserId && (
                <UpdateUserPassword
                    open={passwordModalOpen}
                    onClose={() => setPasswordModalOpen(false)}
                    userId={selectedUserId}
                />
            )}

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleDelete}
                title="Delete User"
                message="Are you sure you want to delete this user? This action cannot be undone."
            />
        </Layout >
    );
};

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 620,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    p: 3,
};

export default UserList;
